import React, { useEffect } from 'react'

import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk'

import type { LaunchDarklyUserDetails } from './buildLaunchDarklyContext'
import { buildLaunchDarklyContext } from './buildLaunchDarklyContext'

interface LaunchDarklyProps {
  onReady: () => void
  children: React.ReactNode
  userDetails: LaunchDarklyUserDetails
  /**
   * True when the signed-in user is acting inside another company (superadmin/support/corporate
   * masquerade). Keeps their own identity out of the `user` context kind — see
   * buildLaunchDarklyContext for why (ENG-988).
   *
   * LDProvider only reads its context at mount (it re-initializes on a context prop change only when
   * `deferInitialization` is set), so this has to move together with something that remounts the
   * provider. In apps/app that's the ApolloProvider `key={cacheScope}` in AuthorizeUser, which is
   * derived from the same masqueradeCompanyId state.
   */
  isMasquerading?: boolean
}

export default function LaunchDarkly({
  onReady,
  children,
  userDetails,
  isMasquerading
}: LaunchDarklyProps): JSX.Element {
  const context = buildLaunchDarklyContext({ userDetails, isMasquerading })

  if (process.env.LAUNCHDARKLY_CLIENT_ID) {
    return (
      <LDProvider clientSideID={process.env.LAUNCHDARKLY_CLIENT_ID} context={context}>
        <LaunchDarklyReadyListener onReady={onReady}>{children}</LaunchDarklyReadyListener>
      </LDProvider>
    )
  } else {
    return <>{children}</>
  }
}
function LaunchDarklyReadyListener({ onReady, children }): JSX.Element {
  const ldClient = useLDClient()

  // If LaunchDarkly doesn't load within 5 seconds, we still want to render the app
  useEffect(() => {
    const timeoutMs = 5000
    let timeout: NodeJS.Timeout | null = null

    if (ldClient) {
      // Start timeout
      timeout = setTimeout(onReady, timeoutMs)
      // Wait for LaunchDarkly or timeout, whichever comes first
      ldClient.waitForInitialization(5).then(() => {
        onReady()
      })
    } else {
      // If ldClient is not available, just use the timeout
      timeout = setTimeout(onReady, timeoutMs)
    }

    // Cleanup timeout if component unmounts before timeout fires
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [ldClient, onReady])

  return <>{children}</>
}
